import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, take } from 'rxjs/operators';

import GET_OFFER_MIX_RESULT from './queries/getOfferMixResultById';
import GET_OFFER_MIX_RESULTS from './queries/getOfferMixResults';
import GET_PLAN_IMPACT from './queries/getPlanImpact';
import { OfferMixResult } from './purchaseplan';
import { Observable, of } from 'rxjs';
import { sellerImpactMock } from '../participants-impact/seller-impact.mock';
import GET_PLAN_IMPACT_SELLER from './queries/getPlanImpactSeller';
import GET_PLAN_IMPACT_SELLER_ALL_TIERS from './queries/getPlanImpactSellerAllTiers';
import GET_PLAN_IMPACT_SELLER_ALL_TIERS_SIO from './queries/getPlanImpactSellerAllTierSIO';
import GET_PLAN_IMPACT_SELLER_SIO from './queries/getPlanImpactSelleSIO';
import { impactMock } from '../participants-impact/impact.mock';
import GET_PLAN_IMPACT_IC_GROUP_LEADER from './queries/getPlanImpacICGroupLeader';
import CHANGE_TIER from './mutations/change-tier';
import SET_INCLUSION_FLAG from './mutations/set-inclusion-flag';
import SET_MIX_STATUS from './mutations/set-mix-status';
import CHANGE_TIER_REOPTIMIZE from './mutations/change-tier-reoptimize';
import GET_PLAN_IMPACT_GC_PARTNER_VIEW from './queries/getPlanImpacGCPartnerView';
import GET_PLAN_IMPACT_SELLER_INDIVIDUAL_COMMITMENT from './queries/getPlanImpactSellerIndividualCommitment';
import { ApolloInstances } from '../graphql.module';
import { MIX_STATUS } from '../../types/rfp/mix-status.enum';
import { PlanImpact, PlanImpacts } from '../../types/rfp/plan-impact.interface';
import GET_PLAN_IMPACT_SELLER_PARTNER_VIEW from './queries/getPlanImpactSellerPartnerView';
import GET_PLAN_IMPACT_SELLER_PARTNER_VIEW_SIO from './queries/getPlanImpactSellerPartnerViewSIO';
import GET_PLAN_IMPACT_SELLER_INDIVIDUAL_COMMITMENT_SIO from './queries/getPlanImpactSellerIndividualCommitmentSIO';
import GET_PLAN_IMPACT_PARTNER_VIEW from './queries/getPlanImpacPartnerView';
import GET_PURCHASEPLAN_MIXES from './queries/getPurchasePlanMixes';
import SELECT_OFFERS from './mutations/update-purchase-plan-offers';
import LOAD_PURCHASE_PLAN from './mutations/load-purchase-plan';
import GET_DOWNLOAD_LID_URL from './queries/getDownloadLID';
import { sortBy } from 'lodash-es';

function sortOrgs(a: any, b: any) {
  if (a.orgHierarchy.length < b.orgHierarchy.length) {
    return -1;
  } else if (a.orgHierarchy.length > b.orgHierarchy.length) {
    return 1;
  } else {
    if (a.orgHierarchy[0] < b.orgHierarchy[0] || a.buyer.name < b.buyer.name) {
      return -1;
    }
    if (a.orgHierarchy[0] > b.orgHierarchy[0] || a.buyer.name > b.buyer.name) {
      return 1;
    }
    // a must be equal to b
    return 0;
  }
}

/**
 * This one follows the pattern of some of the others and keeps the TypeScript types and the queries in separate files.
 * We can always pull them in later if we change to that style. Right now the various implementations are kind of mixed
 * in how they broke up the parts.
 */
@Injectable({
  providedIn: 'root',
})
export class PurchasePlanGraphqlService {
  constructor(private apollo: Apollo) { }

  getOfferMixResult(id: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      .query<{ offerMixResult: OfferMixResult }>({
        fetchPolicy: 'no-cache',
        variables: { id },
        query: GET_OFFER_MIX_RESULT,
      })
      .pipe(map(({ data }) => data.offerMixResult));
  }

  getOfferMixResults(offerId: number, rfpBuyerId: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      .query<{ offerMixResults: OfferMixResult[] }>({
        fetchPolicy: 'no-cache',
        variables: { offerId },
        query: GET_OFFER_MIX_RESULTS,
      })
      .pipe(
        map(({ data }) => {
          let offerMixResults = data.offerMixResults;

          // Darn. I'm realizing now that this does not belong in the GraphQL area but rather in the
          // code which uses it. It's honestly too specific to its display in ag-Grid. But kinda late
          // to extract it right now. When I'm back here again, this should migrate over to the
          // opportunities-page.component.ts. Note: I'm on the fence as to whether that includes the
          // sorting though.

          // There isn't exactly a "hierarchy" in the data, but we can figure out which one is the group because it has
          // a buyer ID that matches the RFP.
          const groupIndex = offerMixResults.findIndex((offerMixResult) => {
            // Another error we're seeing is the occasional returned data with no buyer info, no commitment type, etc.
            if (offerMixResult?.buyer?.id === undefined) {
              console.log(`No buyer info for Offer ID: ${offerMixResult.id}`);
            }

            return offerMixResult.buyer.id === rfpBuyerId;
          });
          // OK, it seems we're regularly having an error where we don't get back a rollup record.
          // This checks for that specifically and issues some additional logging to help identify it.
          if (groupIndex === -1) {
            console.log(
              `Rollup record not found; should have had one record with offerMixResult.buyer.id === ${rfpBuyerId}`,
            );
            console.log(
              `Offer ID: ${offerMixResults[0].offer.id} Offer Name: ${offerMixResults[0].offer.name}`,
            );
          }
          offerMixResults = offerMixResults.map((offerMixResult) => {
            // By having a hierarchy composed of IDs, I don't have to worry about duplicates occurring
            // (and freaking out ag-Grid) because multiple offers won't produce hierarchies that it
            // views as duplicates.
            (offerMixResult as any).orgHierarchy = [
              offerMixResults[groupIndex]?.offer.id,
              offerMixResult.buyer.id,
            ];

            offerMixResult = { ...this.mapOfferTierMixResult(offerMixResult) };
            return offerMixResult;
          });

          // This overwrites the hierarchy for the aggregation because we want it to only have one
          // value. All of the partners should have a hierarchy with two values.
          if (groupIndex !== -1) {
            (offerMixResults[groupIndex] as any).orgHierarchy = [
              offerMixResults[groupIndex].offer.id,
            ];
          }

          offerMixResults.sort(sortOrgs);

          return offerMixResults;
        }),
      );
  }

  mapOfferTierMixResult(offerMixResult: OfferMixResult): OfferMixResult {
    const tiers = offerMixResult.tiers.map(tier => tier.tier);
    const sortedTiers = sortBy(tiers, ['order']);
    const hasTierOrderZero = sortedTiers[0]?.order === 0;

    if (hasTierOrderZero) {
      const updatedTiers = offerMixResult.tiers.map(tier => ({
        ...tier,
        tier: {
          ...tier.tier,
          order: tier.tier.order + 1
        }
      }));

      return {
        ...offerMixResult,
        tiers: updatedTiers
      };
    }

    return offerMixResult;
  }


  //Buyer
  getPlanImpact(rfpId: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.query<any>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_PLAN_IMPACT,
      })
      .pipe(
        map(({ data }) => {
          return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
        }),
      );
  }

  getPlanImpactSellerAllTiers(
    rfpId: number,
    offerId: number,
    primaryCrosses: boolean,
    isSIO: boolean,
    isIndividualCommitment: boolean,
  ) {
    if (isSIO && !isIndividualCommitment) {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, offerId, primaryCrosses },
          query: GET_PLAN_IMPACT_SELLER_ALL_TIERS_SIO,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
          }),
        );
    } else if (isSIO && isIndividualCommitment) {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, offerId, primaryCrosses },
          query: GET_PLAN_IMPACT_SELLER_INDIVIDUAL_COMMITMENT_SIO,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersPartnerLevel(data.planImpact) : data;
          }),
        );
    } else if (isIndividualCommitment) {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, offerId, primaryCrosses },
          query: GET_PLAN_IMPACT_SELLER_INDIVIDUAL_COMMITMENT,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersPartnerLevel(data.planImpact) : data;
          }),
        );
    } else {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, offerId, primaryCrosses },
          query: GET_PLAN_IMPACT_SELLER_ALL_TIERS,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
          }),
        );
    }
  }

  getPlanImpactSellerPartnerView(
    rfpId: number,
    offerId: number,
    primaryCrosses: boolean,
    memberId: number,
    tierId: number,
    isSIO: boolean,
  ) {
    if (isSIO) {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, offerId, primaryCrosses, memberId, tierId },
          query: GET_PLAN_IMPACT_SELLER_PARTNER_VIEW_SIO,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
          }),
        );
    } else {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, offerId, primaryCrosses, memberId, tierId },
          query: GET_PLAN_IMPACT_SELLER_PARTNER_VIEW,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
          }),
        );
    }
  }

  getPlanImpactSellerMock() {
    return of(sellerImpactMock).pipe(
      map(({ data }) => {
        return data;
      }),
    );
  }

  getPlanImpactMock() {
    return of(impactMock).pipe(
      map(({ data }) => {
        return data;
      }),
    );
  }

  // Buyer
  getPlanImpactICGroupLeader(rfpId: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.query<any>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_PLAN_IMPACT_IC_GROUP_LEADER,
      })
      .pipe(
        map(({ data }) => {
          return (this.hasTierOrderZero(data)
            ?
            (data.planImpact[0]?.selectedTier === null ? this.mapTierOrdersPartnerLevel(data.planImpact)
              : this.mapTierOrdersGroupLevel(data.planImpact)) :
            data);
        }),
        map((result) =>
          this.addInclusionStatusToPlanImpacts(result.planImpact),
        ),
      );
  }

  // Buyer
  getPlanImpactGCPartnerView(rfpId: number, memberId?: number) {
    if (memberId) {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, memberId },
          query: GET_PLAN_IMPACT_GC_PARTNER_VIEW,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
          }),
        );
    } else { //here
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.query<any>({
          fetchPolicy: 'no-cache',
          variables: { rfpId, memberId },
          query: GET_PLAN_IMPACT_PARTNER_VIEW,
        })
        .pipe(
          map(({ data }) => {
            return this.hasTierOrderZero(data) ? this.mapTierOrdersGroupLevel(data.planImpact) : data;
          }),
        );
    }
  }

  setInclusionFlag(
    rfpId: number,
    offerId: number,
    included: string,
    memberId?: number,
  ) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.mutate<{ setInclusionFlag: string }>({
        variables: { rfpId, memberId: memberId ?? null, offerId, included },
        mutation: SET_INCLUSION_FLAG,
      })
      .pipe(
        map(({ data }) => {
          return data;
        }),
      );
  }

  private addInclusionStatusToPlanImpacts(planImpacts: PlanImpact[]) {
    return {
      planImpact: planImpacts.map((planImpact: PlanImpact) => {
        return {
          ...planImpact,
          planImpacts: planImpact.planImpacts?.map(
            (planImpact: PlanImpacts) => {
              planImpact.results = {
                inclusionStatus: planImpact.inclusionStatus,
                ...planImpact.results,
              };
              return planImpact;
            },
          ),
        };
      }),
    };
  }

  // graph QL call
  changeTier(
    activePurchasePlan: number,
    offerId: number,
    tierId: number,
    reoptimize = false,
  ) {
    if (reoptimize) {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.mutate<{ updated: boolean }>({
          variables: { activePurchasePlan, offerId, tierId },
          mutation: CHANGE_TIER_REOPTIMIZE,
        })
        .pipe(
          map(({ data }) => {
            return data;
          }),
        );
    } else {
      return this.apollo
        .use(ApolloInstances.PURCHASEPLAN)
        ?.mutate<{ updated: boolean }>({
          variables: { activePurchasePlan, offerId, tierId },
          mutation: CHANGE_TIER,
        })
        .pipe(
          map(({ data }) => {
            return data;
          }),
        );
    }
  }

  setMixStatus(mixStatus: MIX_STATUS, purchasePlanId: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.mutate<{ updateMixStatus: string }>({
        variables: { mixStatus, purchasePlanId },
        mutation: SET_MIX_STATUS,
      })
      .pipe(
        map(({ data }) => {
          return data;
        }),
      );
  }

  getRfpMixes(rfpId: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.query<any>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_PURCHASEPLAN_MIXES,
      })
      .pipe(
        map(({ data }) => {
          return data;
        }),
      );
  }

  setSelectOffers(activePurchasePlanId: number, offers: any) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.mutate<any>({
        variables: {
          purchasePlanId: activePurchasePlanId,
          offers: offers
        },
        mutation: SELECT_OFFERS,
      })
      .pipe(map(({ data }) => data));
  }

  loadPurchasePlan(purchasePlanId: number, planId: number) {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      ?.mutate<any>({
        variables: {
          activePurchasePlanId: purchasePlanId,
          loadPlanRequest: { purchasePlanId: planId }
        },
        mutation: LOAD_PURCHASE_PLAN,
      })
      .pipe(map(({ data }) => data));
  }

  downloadLIDReport(rfpId: string): Observable<string> {
    return this.apollo
      .use(ApolloInstances.PURCHASEPLAN)
      .query<{ downloadLIDReport: { url: string } }>({
        fetchPolicy: 'no-cache',
        variables: { rfpId },
        query: GET_DOWNLOAD_LID_URL,
      })
      .pipe(
        take(1),
        map((request) => request.data.downloadLIDReport.url),
      );
  }

  mapTierOrdersPartnerLevel(planImpact: PlanImpact[]) { //map tierOrders in case starts by 0 instead of 1
    return {
      planImpact: planImpact.map(pi => {
        const sortedTiers = sortBy(pi.offer.tiers, ['order'])
        return ({
          ...pi,
          offer: {
            ...pi.offer,
            tiers: sortedTiers.map(tier => ({ ...tier, order: tier.order + 1 }))
          },
          planImpacts: pi.planImpacts?.map((impact: PlanImpacts) =>
          (
            {
              ...impact,
              selectedTier: {
                ...impact.selectedTier,
                order: (impact.selectedTier?.order ?? 0) + 1
              }
            })
          )
        })
      }
      )
    }
  }

  mapTierOrdersGroupLevel(planImpact: PlanImpact[]) { //map tierOrders in case starts by 0 instead of 1
    return {
      planImpact: planImpact.map(pi => {
        const sortedTiers = sortBy(pi.offer.tiers, ['order'])
        return ({
          ...pi,
          offer: {
            ...pi.offer,
            tiers: sortedTiers.map(tier => ({ ...tier, order: tier.order + 1 }))
          },
          selectedTier: {
            ...pi.selectedTier,
            order: (pi.selectedTier?.order ?? 0) + 1
          }
        })
      })
    }
  }

  hasTierOrderZero(data: any) {
    const tiers = data.planImpact[0].offer.tiers as Array<any>
    const sortedTiers = sortBy(tiers, ['order'])
    return sortedTiers[0]?.order === 0;
  }


}
